import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixSearchPlatformizedV1SiteSearchPlatformizedServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/_api/site-search',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/site-search',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/site-search',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/site-search',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_search_wix-site-search';

/**
 * Retrieves a list of site documents that match the provided search query and optionally performs aggregations on the data queried.
 *
 * The `search()` API supports the document types listed in the [Introduction](https://dev.wix.com/docs/sdk/backend-modules/search/wix-site-search/introduction), each with its own schema. These schemas define the fields available for filtering, sorting, and free-text searching.
 *
 * To learn more about working with the search query, see [API Query Language](https://dev.wix.com/docs/sdk/articles/working-with-the-sdk/api-query-language).
 */
export function search(payload: object): RequestOptionsFactory<any> {
  function __search({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.platformized.v1.site_document',
      method: 'POST' as any,
      methodFqn:
        'wix.search.platformized.v1.SiteSearchPlatformizedService.Search',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchPlatformizedV1SiteSearchPlatformizedServiceUrl({
        protoPath: '/v1/search',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __search;
}

/**
 * Provides phrase completion. For example "blu" could return "blue", "blues" and "blunt" as candidate phrases.
 * This operation is resource heavy at index time and is reserved for special use cases.
 */
export function autocomplete(payload: object): RequestOptionsFactory<any> {
  function __autocomplete({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.platformized.v1.site_document',
      method: 'POST' as any,
      methodFqn:
        'wix.search.platformized.v1.SiteSearchPlatformizedService.Autocomplete',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchPlatformizedV1SiteSearchPlatformizedServiceUrl({
        protoPath: '/v1/autocomplete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __autocomplete;
}

/**
 * Executes search query to fetch suggested items. Unlike search query, suggest will match
 * partial phrases (for example "blu" will match documents containing "blue", "blues" and "blunt").
 * Phrase needs to be at least 3 symbols long. Suggestions can also perform optimizations in search
 * results and generally do not guarantee the same level of quality as regular Search endpoint.
 */
export function suggest(payload: object): RequestOptionsFactory<any> {
  function __suggest({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.search.platformized.v1.site_document',
      method: 'POST' as any,
      methodFqn:
        'wix.search.platformized.v1.SiteSearchPlatformizedService.Suggest',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSearchPlatformizedV1SiteSearchPlatformizedServiceUrl({
        protoPath: '/v1/suggest',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __suggest;
}
